import { useContext, useLayoutEffect, useMemo } from "react";
import { Outlet } from "react-router-dom";
import PropTypes from "prop-types";
import { Box, ThemeProvider, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/system";
import RewampAppBar from "../components/RewampAppBar";
import RewampRouteAnnouncer from "../components/RewampRouteAnnouncer";
import { RewampFooter } from "../components/RewampFooter";
import { AppLayoutContext } from "../../contexts/AppLayoutContext";
import { useDisplayMode } from "../context/DisplayModeContext";
import { usePwaChrome } from "../context/PwaChromeContext";
import { applyNavHeight, bottomBarOffset } from "./navMetrics";
import { PWA_LAYOUT } from "./pwaConfig";
import generateRewampTheme from "../theme";

export default function RewampLayout({ chrome = "full" }) {
  const {
    handleLogout,
    decreaseFontSize,
    increaseFontSize,
    resetTextSize,
    ourTheme,
    initData,
    fontSize,
  } = useContext(AppLayoutContext);

  const isMdUp = useMediaQuery(useTheme().breakpoints.up("md"));
  const { standalone } = useDisplayMode();
  const { header: pwaHeader } = usePwaChrome();
  const showChrome = chrome === "full";

  // In the app the PWA shell owns the nav height.
  useLayoutEffect(() => (standalone ? undefined : applyNavHeight(chrome)), [chrome, standalone]);

  // Regenerates only when fontSize or ourTheme changes — same pattern as App.js
  const rewampTheme = useMemo(
    () => generateRewampTheme(fontSize, ourTheme),
    [fontSize, ourTheme]
  );

  return (
    <ThemeProvider theme={rewampTheme}>
      <RewampRouteAnnouncer />
      {showChrome && (
        <RewampAppBar
          handleLogout={handleLogout}
          decreaseFontSize={decreaseFontSize}
          increaseFontSize={increaseFontSize}
          ourTheme={ourTheme}
          resetTextSize={resetTextSize}
        />
      )}
      {/* tabIndex=-1 lets the "Skip to main content" link focus this landmark; outline:none hides the ring on that focus. */}
      <Box
        component="main"
        className={`app-body ${isMdUp ? "app-body-md" : ""}`}
        id="main-portal"
        tabIndex={-1}
        sx={{
          outline: "none",
          ...(standalone && {
            backgroundColor: "background.tint",
            pb: bottomBarOffset(0),
            ...(!pwaHeader && { pt: PWA_LAYOUT.topInset }),
          }),
        }}
      >
        <Outlet />
      </Box>
      {showChrome && <RewampFooter initData={initData} />}
    </ThemeProvider>
  );
}

RewampLayout.propTypes = {
  chrome: PropTypes.oneOf(["full", "none"]),
};
